<script setup lang="ts">
import { ref } from 'vue';
import { onClickOutside } from '@vueuse/core';

import {
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { AppRouterLink, AppStars } from '@libs/ui';

import type { HotelMapCardData } from '../models';

interface HotelMapCardProps {
  data: HotelMapCardData;
}

const props = defineProps<HotelMapCardProps>();

interface HotelMapCardEmits {
  (eventName: 'click'): void;
  (eventName: 'hide'): void;
}

const emit = defineEmits<HotelMapCardEmits>();

const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();

const hotelMapCard = ref<HTMLDivElement | undefined>(undefined);

onClickOutside(hotelMapCard, () => emit('hide'));

function onNavigationTriggered(): void {
  statsigEventsStore.logHotelSelectionMapCardClicked();
}
</script>

<template>
  <div ref="hotelMapCard" class="hotel-map-card" @click="emit('click')">
    <div class="image-wrapper">
      <img
        :src="props.data.imageUrl"
        :alt="$t('a11y.hotel_image')"
        class="image"
      />
    </div>
    <div class="content">
      <AppRouterLink
        :link="
          routerStore.getHotelDetailsUrlInHotelSelection(
            { ingredientIndex: props.data.ingredientIndex },
            props.data.hotelCode
          )
        "
        font="font-label-s-bold"
        color="color-neutral-70"
        @navigation-triggered="onNavigationTriggered"
      >
        {{ props.data.hotelName }}
      </AppRouterLink>
      <div class="stars-rating">
        <AppStars :amount="props.data.starsRating" size="18" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-map-card {
  width: 256px;
  height: 198px;

  box-shadow: var(--shadow);

  display: flex;
  flex-flow: column;

  .image-wrapper {
    flex-grow: 1;
    overflow: hidden;
    width: 100%;
    border-top-left-radius: var(--border-radius-12);
    border-top-right-radius: var(--border-radius-12);

    .image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .content {
    padding: var(--spacing-12);
    background: var(--color-white);
    border-bottom-left-radius: var(--border-radius-12);
    border-bottom-right-radius: var(--border-radius-12);
    transition: background var(--transition-short);

    display: flex;
    flex-flow: column;
    gap: var(--spacing-8);
  }
  @include on-hover {
    cursor: pointer;

    .content {
      background: var(--color-neutral-30);
    }
  }
}
</style>
